<script lang="ts">
  import type { SourceInput } from '@multiview/shared';
  import { api } from '../lib/api/client.ts';

  interface Props {
    onDone: () => void;
    onLogout: () => void;
  }
  const { onDone, onLogout }: Props = $props();

  let type = $state<'xtream' | 'm3u' | 'hls'>('xtream');
  let name = $state('');
  let url = $state('');
  let username = $state('');
  let password = $state('');

  let busy = $state(false);
  let error = $state<string | null>(null);
  let success = $state<string | null>(null);

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    busy = true;
    error = null;
    success = null;

    const input: SourceInput =
      type === 'xtream'
        ? { type: 'xtream', name: name.trim(), url: url.trim(), username: username.trim(), password }
        : type === 'm3u'
          ? { type: 'm3u', name: name.trim(), url: url.trim() }
          : { type: 'hls', name: name.trim(), url: url.trim() };

    try {
      const result = await api.addSource(input);
      success = `${result.channels} Kanäle importiert.`;
      // The EPG is large and not needed to start watching, so it is kicked off
      // without blocking.
      void api.refreshEpg().catch(() => {});
      setTimeout(onDone, 700);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      busy = false;
    }
  }
</script>

<div class="setup scroll">
    <div class="card">
    <header class="head">
      <h1>Quelle einrichten</h1>
      <button data-focusable type="button" onclick={onLogout}>Abmelden</button>
    </header>
    <p class="muted">
      Multiview spielt nur, was du selbst mitbringst. Zugangsdaten bleiben auf deinem Proxy
      und werden nie an den Browser zurückgegeben.
    </p>

    <form onsubmit={submit}>
      <label>
        <span>Art</span>
        <select bind:value={type} data-focusable data-focus-default>
          <option value="xtream">Xtream Codes (Host + Benutzer + Passwort)</option>
          <option value="m3u">M3U-Playlist (URL oder Dateipfad)</option>
          <option value="hls">Einzelner HLS-Stream (.m3u8)</option>
        </select>
      </label>

      <label>
        <span>Name</span>
        <input bind:value={name} data-focusable required placeholder="Mein Anbieter" />
      </label>

      <label>
        <span>{type === 'xtream' ? 'Portal-URL' : type === 'm3u' ? 'Playlist-URL oder Pfad' : 'Stream-URL'}</span>
        <input
          bind:value={url}
          data-focusable
          required
          placeholder={type === 'xtream' ? 'http://beispiel.tv:8080' : 'http://… .m3u8'}
        />
      </label>

      {#if type === 'xtream'}
        <div class="pair">
          <label>
            <span>Benutzer</span>
            <input bind:value={username} data-focusable required autocomplete="off" />
          </label>
          <label>
            <span>Passwort</span>
            <input bind:value={password} data-focusable required type="password" autocomplete="off" />
          </label>
        </div>
      {/if}

      {#if error}<p class="error">{error}</p>{/if}
      {#if success}<p class="success">{success}</p>{/if}

      <button data-focusable type="submit" disabled={busy} class="primary">
        {busy ? 'Importiere …' : 'Quelle hinzufügen'}
      </button>
    </form>
  </div>
</div>

<style>
  .setup {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--safe-inset);
  }

  .card {
    width: min(46em, 100%);
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: calc(var(--pad) * 1.5);
  }

  .head { display: flex; align-items: center; justify-content: space-between; gap: var(--pad); }
  h1 { margin: 0 0 0.4em; font-size: var(--font-xl); }
  p.muted { margin: 0 0 1.5em; line-height: 1.5; }

  form { display: flex; flex-direction: column; gap: var(--pad); }
  label { display: flex; flex-direction: column; gap: 6px; }
  label > span { font-size: var(--font-sm); color: var(--text-dim); }

  .pair { display: grid; grid-template-columns: 1fr 1fr; gap: var(--pad); }

  .primary { background: var(--accent-dim); border-color: var(--accent); font-weight: 600; }
  .primary:disabled { opacity: 0.6; }

  .error { margin: 0; color: var(--danger); }

  .success { margin: 0; color: var(--accent); }
</style>
